// Robust browser download helper.
// Uses fetch → blob → object URL → programmatic anchor click.
// Works inside sandboxed iframes (Emergent preview) where the native
// `<a href download>` attribute can be silently blocked.

import { toast } from "sonner";

export async function downloadFile(url, filename, onProgress) {
  let controller;
  try {
    controller = new AbortController();
    const res = await fetch(url, {
      credentials: "include",
      signal: controller.signal,
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j.detail || msg;
      } catch {
        /* plain non-json body */
      }
      throw new Error(msg);
    }
    // Prefer server-reported filename where available.
    const cd = res.headers.get("content-disposition") || "";
    const m = /filename\*=utf-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i.exec(cd);
    const serverName = m ? decodeURIComponent(m[1] || m[2] || m[3] || "") : "";
    const finalName = serverName || filename || "download.bin";

    // Stream into a blob — for the ~1MB PDFs / ~500KB ZIPs we have, this is plenty.
    const total = Number(res.headers.get("content-length") || 0);
    if (total && onProgress && res.body) {
      const reader = res.body.getReader();
      const chunks = [];
      let received = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        onProgress(received, total);
      }
      const blob = new Blob(chunks, { type: res.headers.get("content-type") || "application/octet-stream" });
      return triggerSave(blob, finalName);
    }
    const blob = await res.blob();
    return triggerSave(blob, finalName);
  } catch (e) {
    // Don't re-throw "abort" as an error
    if (e.name === "AbortError") return;
    toast.error(`Download failed: ${e.message}`);
    throw e;
  } finally {
    if (controller) controller = null;
  }
}

function triggerSave(blob, filename) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Safari needs a tick before we revoke the URL.
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(href);
  }, 1200);
  return filename;
}
